import { type Virtualizer } from "@tanstack/react-virtual";

import type {
  TableColumn,
  TableColumnGroup,
  TablePaginationType,
  TableRowType,
  TableSort,
} from "./Table.types";

export const getDefaultRowId = <Row extends TableRowType>(
  _row: Row,
  rowIndex: number,
  cardIndex?: number /* on mobile we need to pass cardIndex as each card has multiple rows */,
) => rowIndex.toString() + (cardIndex !== undefined ? `-${cardIndex}` : "");

export const getDefaultCardId = <Row extends TableRowType>(
  _row: Row,
  cardIndex: number,
) => cardIndex.toString();

export const getDefaultGroupId = <
  Row extends TableRowType,
  Group extends string = never,
>(
  _row: Row,
  group: Group,
  rowIndex: number /* on mobile cardIndex plays the role of rowIndex since the hierarchy is different */,
) => `${rowIndex.toString()}-${group}`;

export const getColumnGroups = <
  Row extends TableRowType,
  Group extends string = never,
>(
  columns: TableColumn<Row, Group>[],
): TableColumnGroup<Group>[] => {
  return columns.reduce<TableColumnGroup<Group>[]>((groups, column, index) => {
    if (
      groups[groups.length - 1]?.group === column.group &&
      column.group !== undefined
    ) {
      groups[groups.length - 1].end = index;
    } else {
      groups.push({ group: column.group, start: index, end: index });
    }

    return groups;
  }, []);
};

export const sortData = <
  Row extends TableRowType,
  Group extends string = never,
>({
  data,
  sort,
  columns,
}: {
  data: Row[];
  sort: TableSort<Row>;
  columns: TableColumn<Row, Group>[];
}) => {
  const sortStrategy = columns.find((column) => column.id === sort.key)?.sort;

  return [...data].sort((a, b) => {
    const aValue = a[sort.key as keyof Row];
    const bValue = b[sort.key as keyof Row];
    let result;

    if (typeof sortStrategy === "function") {
      result = sortStrategy(a, b);
    } else if (sortStrategy === "number") {
      result = (aValue as number) - (bValue as number);
    } else if (sortStrategy === true) {
      // Infer sort type from runtime value
      if (typeof aValue === "number" && typeof bValue === "number") {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }
    } else {
      // "string" or undefined defaults to string sorting
      result = String(aValue).localeCompare(String(bValue));
    }

    return sort.order === "asc" ? result : -result;
  });
};

export const paginateData = <Row extends TableRowType>({
  data,
  pagination: { page, pageSize },
}: {
  data: Row[];
  pagination: TablePaginationType;
}) => {
  const start = (page - 1) * pageSize;

  return data.slice(start, start + pageSize);
};

export const getTableMinWidth = <
  Row extends TableRowType,
  Group extends string = never,
>({
  columns,
  minCellWidth,
}: {
  columns: TableColumn<Row, Group>[];
  minCellWidth: string;
}): string => {
  const minWidths = columns.map((column) => column.minWidth ?? minCellWidth);

  // Join all minimum widths with calc() addition
  return minWidths.length > 1
    ? `calc(${minWidths.join(" + ")})`
    : minWidths[0] || "0px";
};

export const getRows = <Row extends TableRowType>({
  data,
  virtualizer,
  hasVirtualization,
}: {
  data: Row[];
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  hasVirtualization: boolean;
}) => {
  return hasVirtualization
    ? virtualizer.getVirtualItems().map((item) => ({
        row: data[item.index],
        styles: {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `${item.size}px`,
          transform: `translateY(${item.start}px)`,
        },
      }))
    : data.map((row) => ({
        row,
        styles: {},
      }));
};
